import { next, schedule } from '@ember/runloop';

import $ from 'jquery';

import type { DChartInterface, DSSetup, PopupData } from 'webapp-types';
import { AnnotationType, Direction, PopupCloseReason, PopupEvent } from 'webapp/enums/enums';
import { getBaseAnomalyVariant } from 'webapp/utils/anomaly-detection';

import AnnotationPopupEditComponent from 'webapp/components/annotation/popup-edit';
import AnnotationPopupListComponent from 'webapp/components/annotation/popup-list';

import type { DataPointInfo } from 'webapp/models-custom/annotation';
import Annotation from 'webapp/models-custom/annotation';
import type DataSelect from 'webapp/models-custom/data-select';
import MetricSetup from 'webapp/models-custom/metric-setup';
import type GridRenderer from 'webapp/d-chart/renderer/grid';
import type HeatmapRenderer from 'webapp/d-chart/renderer/heatmap';

type RendererWithAnnotations = GridRenderer | HeatmapRenderer;

export default abstract class AnnotationsHandlerBase {
	renderer: RendererWithAnnotations;
	hChart!: Highcharts.Chart;
	eventCatcherEl: JQuery | null = null;

	annotatablePointsIds: string[] = []; // representing ids (stringified timestamps) of points to which we can add annotation(s)
	annotatablePointsIdsWithAnnotations: string[] = []; // ids of points that already have annotation(s) (subset of annotatablePointsIds)
	addBtnAnnotatablePoint: string | null = null;
	isHoveringOverAddButton = false;
	isHoveringOverPoint = false;
	isEditFormOpen = false;

	constructor(renderer: RendererWithAnnotations) {
		this.renderer = renderer;
	}

	get canAnnotate(): boolean {
		return this.renderer.dChart.canAnnotate;
	}

	attachToChart(hChart: Highcharts.Chart): void {
		this.hChart = hChart;
	}

	update(): void {
		this.updateLabelPos();
	}

	// ------ EXISTING ANNOTATION ICONS ------

	prepareAnnotationsData(): DChartInterface.AnnotationOptions[] {
		const that = this,
			annotationsData: DChartInterface.AnnotationOptions[] = [],
			annotationsByAxisTs = this.renderer.annotationsByAxisTs;

		this.annotatablePointsIds = Object.keys(annotationsByAxisTs);
		this.annotatablePointsIdsWithAnnotations = this.annotatablePointsIds.filter((ts) => {
			const annotations = annotationsByAxisTs[ts];
			return annotations && annotations.length > 0;
		});

		this.annotatablePointsIdsWithAnnotations.forEach((ts) => {
			const annotations = annotationsByAxisTs[ts],
				annotationCount = annotations?.filter(
					(annotation) => !annotation.isAnomalyType,
				).length;

			const anomalyAnnotation = annotations?.find((a) => a.isAnomalyType);
			let anomalyVariant;

			if (anomalyAnnotation?.metricSetup.metricSetupData) {
				const ds = this.getDataselectByMetricSetupData(
					anomalyAnnotation.metricSetup.metricSetupData,
				);

				const extra = anomalyAnnotation.extra;
				anomalyVariant =
					extra && ds?.dataRoot
						? getBaseAnomalyVariant(
								ds.dataRoot.invert
									? extra.score < extra.lowerValue
									: extra.score > extra.upperValue,
								extra.score,
							)
						: null;
			}

			const annotationIconTextStyle = anomalyAnnotation
				? `style='color: var(--${anomalyVariant?.color}); opacity: 1;'`
				: '';

			const annotationType = anomalyAnnotation
				? `dbx-annotation-label--${AnnotationType.ANOMALY}`
				: `dbx-annotation-label--${AnnotationType.DEFAULT}`;
			const annotationIconText = `<span class="dbx-annotation-label dbx-annotation-label--view ${annotationType}" ${annotationIconTextStyle} ><ui-icon name="AnnotationEmptyFill" size="dynamic"></ui-icon><em>${anomalyAnnotation ? '!' : annotationCount}</em></span>`;

			annotationsData.push({
				animation: false,
				draggable: '',
				events: {
					remove: function (this: Highcharts.Annotation, _event?: object) {
						try {
							const el = (
								this as DChartInterface.AnnotationWithHtmlLabel
							).graphic.div!.querySelector('.dbx-annotation-label')!;
							$(el).tooltipster('destroy');
						} catch (_e) {
							// skip
						}
					},
					click: function (this: Highcharts.Annotation, event?: object) {
						// if clicked on the already opened popup at the same point, toggle fixed prop
						if (that.renderer.popupService.isOpen(AnnotationPopupListComponent)) {
							const popup = that.renderer.popupService.findByComponent(
								AnnotationPopupListComponent,
							);
							if (popup?.data.pointId === ts) {
								popup.triggerEvent('toggleIsFixed');
								schedule('afterRender', popup, popup.reposition);
								return;
							}
						}

						const target = (
							(event as Event).target as HTMLElement
						).closest<HTMLElement>('.dbx-annotation-label'); // event.target is the em

						if (!target) {
							return;
						}

						const openPopupFn = () => {
							// opening annotation list popup
							that.openViewAnnotations(ts, target);
						};

						const activePopup = that.renderer.popupService.activePopup;
						if (activePopup && !activePopup.allowStacked) {
							void activePopup.close();
							// next + afterRender === when previous popup removed from outlet
							next(() => {
								schedule('afterRender', openPopupFn);
							});
						} else {
							openPopupFn();
						}
					},
					mouseenter: function (this: Highcharts.Annotation, event: Event) {
						const activePopup = that.renderer.popupService.activePopup;

						// don't close other popups
						if (
							activePopup &&
							!activePopup?.allowStacked &&
							!that.renderer.popupService.isOpen(AnnotationPopupListComponent)
						) {
							// don't close other popups
							return;
						}

						that.hideAddButton();

						that.attachTooltipToAnnotation(this);

						// don't close fixed popups
						const isActiveAnnotationsListPopupFixed =
							activePopup?.triggerEvent('checkIsFixed')?.[0];
						if (!activePopup || !isActiveAnnotationsListPopupFixed) {
							const target = (event.target as HTMLElement).querySelector<HTMLElement>(
								'.dbx-annotation-label',
							)!; // event.target is annotation container with position somewhere else
							that.openViewAnnotations(ts, target);

							// hide chart tooltip when showing annotations
							$('.highcharts-tooltip-container').css({ opacity: 0 });
						}
					},
					mouseleave: function (this: Highcharts.Annotation, _event: Event) {
						// close only annotations view popup on the same point that we are leaving from
						// do not close fixed popups
						if (that.renderer.popupService.isOpen(AnnotationPopupListComponent)) {
							const popup = that.renderer.popupService.findByComponent(
								AnnotationPopupListComponent,
							);
							if (popup?.data.pointId === ts) {
								const isActiveAnnotationsListPopupFixed =
									popup.triggerEvent('checkIsFixed')?.[0];
								if (!isActiveAnnotationsListPopupFixed) {
									void popup.close(PopupCloseReason.OUTSIDECLICK);
								}
							}
						}

						// show chart tooltip again when closing annotations
						$('.highcharts-tooltip-container').css({ opacity: 1 });
					},
				},
				labelOptions: {
					allowOverlap: true, // when datalabels are enabled - e.g. heatmap shown values on cells
					useHTML: true,
					text: annotationIconText,
					shape: 'rect',
					padding: 0,
					distance: 0,
					y: 0,
				},
				labels: [
					{
						// x, y pos will be set in chart load (via updateLabelPos)
						point: { x: 0, y: 0 } as Highcharts.AnnotationMockPointOptionsObject,
					},
				],
				// our custom additions below
				annotatablePointId: ts,
			});
		});

		return annotationsData;
	}

	protected openViewAnnotations(annotatablePointId: string, targetElement: HTMLElement): void {
		this.renderer.hideTooltip();

		const model = this.renderer.annotationsByAxisTs[annotatablePointId];

		if (!model) {
			return;
		}

		const popupData: PopupData.Annotation.List = {
			model,
			pointId: annotatablePointId,
			canEdit: this.canAnnotate,
			create: () => {
				this.openAddAnnotation(annotatablePointId, targetElement);
			},
			edit: (model: Annotation) => {
				this.openEditAnnotation(model, annotatablePointId, targetElement);
			},
			delete: (model: Annotation) => {
				this.updateAnnotationInData(model.id, null);
			},
			getAnomalyScoreColors: (
				metricSetupData: DSSetup.MetricSetupData,
				value: number,
				lowerValue: number,
				upperValue: number,
			) => {
				const ds = this.getDataselectByMetricSetupData(metricSetupData);
				return getBaseAnomalyVariant(
					ds?.dataRoot?.invert ? value < lowerValue : value > upperValue,
					value,
				);
			},
			getDataselectByMetricSetupData: (metricSetupData: DSSetup.MetricSetupData) => {
				return this.getDataselectByMetricSetupData(metricSetupData);
			},
		};

		const popup = this.renderer.popupService.createContext(
			AnnotationPopupListComponent,
			targetElement,
			{
				popupClass: 'popup-annotations',
				isAnimated: false,
				directions: [Direction.UP, Direction.DOWN, Direction.RIGHT, Direction.LEFT],
				// offset because of hidden arrow
				offset: 6,
				hideArrow: true,
			},
		);

		popup.open(popupData);
	}

	private updateLabelPos(): void {
		// after chart is rendered, must update annotation x,y positions
		const hc = this.hChart;

		if (!hc?.series.length) {
			return;
		}

		(hc as DChartInterface.ChartWithAnnotations).annotations!.forEach((annotation) => {
			const annotationUserOptions =
				annotation.userOptions as DChartInterface.AnnotationOptions;
			let labelPoint: Highcharts.AnnotationMockPointOptionsObject | null = null;

			if (annotationUserOptions.labels) {
				labelPoint = annotationUserOptions.labels[0]
					?.point as Highcharts.AnnotationMockPointOptionsObject;
			}

			if (labelPoint && annotationUserOptions.annotatablePointId) {
				const point = this.getLabelPointForAnnotatablePoint(
					annotationUserOptions.annotatablePointId,
				) as Highcharts.AnnotationMockPointOptionsObject;

				if (point && (labelPoint.x !== point.x || labelPoint.y !== point.y)) {
					annotation.update({
						labels: [
							{
								point,
							},
						],
					});
				}
			}
		});
	}

	// xAxis/yAxis are defined if we want to position x/y to specific coordinates, otherwise pixels are used to position x/y
	protected abstract getLabelPointForAnnotatablePoint(
		annotatablePointId: string,
	): { x?: number; y?: number; xAxis?: number; yAxis?: number } | null; // basically returns Highcharts.AnnotationMockPointOptionsObject

	// ------ ANNOTATION ADD -------

	showAddButton(annotatablePointId: string): void {
		if (!this.hChart) {
			return;
		}

		if (!this.canAnnotate) {
			return;
		}

		if (this.addBtnAnnotatablePoint === annotatablePointId) {
			return;
		}

		if (this.addBtnAnnotatablePoint) {
			this.hChart.removeAnnotation('annotation-add-button');
		}

		if (this.annotatablePointsIdsWithAnnotations.includes(annotatablePointId)) {
			return;
		}

		const point = this.getLabelPointForAnnotatablePoint(
			annotatablePointId,
		) as Highcharts.AnnotationMockPointOptionsObject;

		if (!point) {
			return;
		}

		const that = this;

		this.hChart.addAnnotation({
			id: 'annotation-add-button',
			labels: [
				{
					point,
					useHTML: true,
					text: '<span class="dbx-annotation-label dbx-annotation-label--add"><ui-icon name="AnnotationEmptyFill" size="dynamic"></ui-icon><em>+</em></span>',
					shape: 'rect',
					padding: 0,
					distance: 0,
					y: 0,
				},
			],
			events: {
				click: function (this: Highcharts.Annotation, event: Event) {
					that.renderer.hideTooltip();
					that.openAddAnnotation(annotatablePointId, event.target as HTMLElement);
				},
				mouseover: function () {
					that.isHoveringOverAddButton = true;
				},
				mouseout: function () {
					that.isHoveringOverAddButton = false;
				},
			} as Highcharts.AnnotationsEventsOptions, // mouseover / mouseout aren't in Highcharts' types
		});

		this.addBtnAnnotatablePoint = annotatablePointId;
	}

	hideAddButton(): void {
		if (!this.hChart) {
			return;
		}

		if (!this.isHoveringOverAddButton && !this.isHoveringOverPoint && !this.isEditFormOpen) {
			try {
				// can throw if the hChart is removed
				this.hChart.removeAnnotation('annotation-add-button');
			} catch (_e) {
				// skip
			}
			this.addBtnAnnotatablePoint = null;
		}
	}

	private openAddAnnotation(annotatablePointId: string, targetElement: HTMLElement): void {
		const hc = this.hChart;

		if (!hc) {
			return;
		}

		const timestamp = +annotatablePointId,
			metricSelectionData = this.renderer.getMetricSelectionDataForAxisTs(timestamp),
			firstMetricSelectionData = metricSelectionData[0];

		let dataPointInfo: DataPointInfo, timezone;

		if (firstMetricSelectionData) {
			const metricSetup = new MetricSetup(
				firstMetricSelectionData.metricSetupData || undefined,
			);
			metricSetup.setFiltersFromDimensionsAndValuesMapAndUnit(
				firstMetricSelectionData.dimensionValues,
				firstMetricSelectionData.unit,
			);

			dataPointInfo = {
				timestamp,
				metricSetupData: metricSetup.metricSetupData,
			};
			timezone = this.renderer.dataTimezone;
		} else {
			dataPointInfo = {
				timestamp,
			};
			timezone = 'UTC';
		}

		const annotation = Annotation.createNew(dataPointInfo, timezone);

		this.isEditFormOpen = true;

		const popupData: PopupData.Annotation.Edit = {
			model: annotation,
			metricSelectionData,
			onUpdate: (model: Annotation) => {
				this.updateAnnotationInData(null, model);
			},
		};

		const popup = this.createEditContextPopupInstance(targetElement);

		popup.on(PopupEvent.CLOSED, this, () => {
			this.isEditFormOpen = false;
		});

		popup.open(popupData);

		this.updateAnnotationContext();
	}

	private openEditAnnotation(
		model: Annotation,
		annotatablePointId: string,
		targetElement: HTMLElement,
	): void {
		const timestamp = +annotatablePointId,
			metricSelectionData = this.renderer.getMetricSelectionDataForAxisTs(timestamp);

		this.isEditFormOpen = true;

		const popupData: PopupData.Annotation.Edit = {
			model,
			metricSelectionData,
			onUpdate: (model: Annotation) => {
				this.updateAnnotationInData(null, model);
			},
			onDelete: (model: Annotation) => {
				this.updateAnnotationInData(model.id, null);
			},
		};

		const popup = this.createEditContextPopupInstance(targetElement);

		popup.on(PopupEvent.CLOSED, this, () => {
			this.isEditFormOpen = false;
		});

		popup.open(popupData);

		this.updateAnnotationContext();
	}

	private createEditContextPopupInstance(attachTo: HTMLElement) {
		const popup = this.renderer.popupService.createContext(
			AnnotationPopupEditComponent,
			attachTo,
			{
				popupClass: 'popup-edit-annotation',
				skipAttachToCheck: true,
				// offset because of hidden arrow
				offset: 6,
				hideArrow: true,
			},
		);
		return popup;
	}

	private updateAnnotationContext(): void {
		Annotation.setContext(this.renderer.dChart.annotationContext);
	}

	private updateAnnotationInData(id: number | null, model: Annotation | null): void {
		// this fn makes sure annotation data is added to the sample immediately
		// without waiting for new sample push over socket

		const hc = this.hChart;

		if (!hc) {
			return;
		}

		const sample = this.renderer.visualization.block.sample;
		if (!sample) {
			return;
		}

		const isCreate = !!model && !id;
		const isDelete = !model;

		this.renderer.annotationService.updateSampleAnnotations(sample, id, model);

		this.isEditFormOpen = false;
		this.hideAddButton();

		// if was a new or deleted annotation - redraw the whole annotations band for updated labels
		if (isCreate || isDelete) {
			this.renderer.dChart.updateHChart({
				annotations: this.prepareAnnotationsData(),
			});
		}
	}

	private attachTooltipToAnnotation(annotation: Highcharts.Annotation): void {
		const el = (
			annotation as DChartInterface.AnnotationWithHtmlLabel
		).graphic.div?.querySelector('.dbx-annotation-label');

		if (!el) {
			return;
		}

		const $el = $(el);

		let existing = null;
		try {
			existing = $el.tooltipster('instance');
		} catch (_e) {
			// skip
		}

		if (existing) {
			return;
		}

		let annotationActionText = 'edit';
		if ($el.hasClass(`dbx-annotation-label--${AnnotationType.DEFAULT}`)) {
			annotationActionText = this.canAnnotate ? 'edit' : 'view';
		}

		$el.tooltipster({
			content: `Click to ${annotationActionText} annotations`,
			theme: 'tooltipster-default',
			position: 'bottom',
			delay: 300,
		});
		$el.tooltipster('instance')?.show();
	}

	private getDataselectByMetricSetupData(
		metricSetupData: DSSetup.MetricSetupData,
	): DataSelect | undefined {
		return this.renderer.visualization.blockSetup.dataSelectsShown.find((ds) => {
			return (
				ds.metricSetup.key === metricSetupData.key &&
				ds.metricSetup.spaceAccessId === metricSetupData.spaceAccessId
			);
		});
	}

	destroy(): void {
		// destroying tooltips
		((this.hChart as DChartInterface.ChartWithAnnotations).annotations || []).forEach((a) => {
			this.hChart.removeAnnotation(a);
		});
	}
}
